import React, { useRef, useEffect } from "react";
import { Bold, Underline, Italic, List, ListOrdered } from "lucide-react";

const Btn = ({ onClick, title, children, testId }) => (
    <button
        type="button"
        title={title}
        onMouseDown={(e) => { e.preventDefault(); onClick(); }}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-oth-ink2 hover:bg-[#EFEFEA] hover:text-oth-ink transition-colors"
        data-testid={testId}
    >
        {children}
    </button>
);

/**
 * Lightweight rich-text editor (contentEditable + execCommand) supporting
 * bold / italic / underline / lists. Emits sanitized-ish HTML via onChange.
 */
export const RichTextEditor = ({ value, onChange, placeholder = "Write your note…", testId = "rich-editor" }) => {
    const ref = useRef(null);

    useEffect(() => {
        if (ref.current && ref.current.innerHTML !== (value || "")) {
            ref.current.innerHTML = value || "";
        }
        // Initialise the editor content once on mount; further edits are uncontrolled.
    }, []);

    const exec = (cmd) => {
        document.execCommand(cmd, false, null);
        if (ref.current) onChange(ref.current.innerHTML);
    };

    return (
        <div className="border border-oth-line/70 rounded-2xl bg-white overflow-hidden" data-testid={`${testId}-wrap`}>
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-oth-line/60 bg-[#FAFAF8]">
                <Btn onClick={() => exec("bold")} title="Bold" testId={`${testId}-bold`}><Bold size={15} /></Btn>
                <Btn onClick={() => exec("italic")} title="Italic" testId={`${testId}-italic`}><Italic size={15} /></Btn>
                <Btn onClick={() => exec("underline")} title="Underline" testId={`${testId}-underline`}><Underline size={15} /></Btn>
                <div className="w-px h-5 bg-oth-line/70 mx-1" />
                <Btn onClick={() => exec("insertUnorderedList")} title="Bullet list" testId={`${testId}-ul`}><List size={15} /></Btn>
                <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" testId={`${testId}-ol`}><ListOrdered size={15} /></Btn>
            </div>
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => onChange(e.currentTarget.innerHTML)}
                data-placeholder={placeholder}
                className="rt-editor min-h-[120px] px-4 py-3 font-body text-sm text-oth-ink focus:outline-none leading-relaxed"
                data-testid={testId}
            />
        </div>
    );
};
